import { useState, useMemo, useEffect } from "react";
import { createPortal } from "react-dom";
import { MOCK_PRESCRIPTIONS, MOCK_REFILL_REQUESTS } from "./MedicationsTab";
import { db } from "../lib/db";
import { useApp } from "../context/AppContext";

const isDemoMode = process.env.REACT_APP_DEMO_MODE === 'true';

// ─── DESIGN TOKENS ────────────────────────────────────────────────────────────
const C = {
  navy:   "var(--navy)", navy2: "var(--navy-2)", navy3: "var(--navy-3)", navy4: "var(--navy-4)",
  teal:   "#0ea5a0", tealLt: "rgba(14,165,160,0.12)",
  amber:  "#f0a500", amberLt: "rgba(240,165,0,0.15)",
  red:    "#e05252", redLt: "rgba(224,82,82,0.12)",
  green:  "#22c55e", greenLt: "rgba(34,197,94,0.12)",
  purple: "#a855f7", purpleLt: "rgba(168,85,247,0.12)",
  text1:  "var(--text-1)", text2:  "var(--text-2)", text3: "var(--text-3)",
  border: "var(--border)", border2: "var(--border-2)",
};

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const fmtDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const daysSince = (iso) => {
  if (!iso) return 0;
  return Math.floor((Date.now() - new Date(iso + "T12:00:00").getTime()) / 86400000);
};

const scheduleColor = (s) => {
  if (s === "C-II")  return { color: "#ff4444", bg: "rgba(255,68,68,0.15)" };
  if (s === "C-III") return { color: C.red,     bg: C.redLt };
  if (s === "C-IV")  return { color: C.amber,   bg: C.amberLt };
  if (s === "C-V")   return { color: "#facc15", bg: "rgba(250,204,21,0.12)" };
  return { color: C.text3, bg: "transparent" };
};

// ─── SUBCOMPONENTS ────────────────────────────────────────────────────────────
function ScheduleBadge({ schedule }) {
  if (!schedule) return null;
  const { color, bg } = scheduleColor(schedule);
  return (
    <span style={{
      fontSize: 10, fontWeight: 600, padding: "2px 7px", borderRadius: 4,
      color, background: bg, border: `1px solid ${color}40`, letterSpacing: "0.04em",
    }}>{schedule}</span>
  );
}

function StatusBadge({ status }) {
  const map = {
    active:        { label: "Active",       color: C.green, bg: C.greenLt },
    completed:     { label: "Completed",    color: C.text2, bg: "rgba(255,255,255,0.06)" },
    discontinued:  { label: "Discontinued", color: C.red,   bg: C.redLt },
    pending:       { label: "Pending",      color: C.amber, bg: C.amberLt },
    approved:      { label: "Approved",     color: C.teal,  bg: C.tealLt },
    denied:        { label: "Denied",       color: C.red,   bg: C.redLt },
  };
  const s = map[status] || map.active;
  return (
    <span style={{
      fontSize: 10, fontWeight: 600, padding: "2px 7px", borderRadius: 4,
      color: s.color, background: s.bg,
    }}>{s.label}</span>
  );
}

function PdmpBadge({ checked }) {
  return (
    <span style={{
      fontSize: 10, fontWeight: 600, padding: "2px 7px", borderRadius: 4,
      color: checked ? C.green : C.red,
      background: checked ? C.greenLt : C.redLt,
    }}>{checked ? "✓ Checked" : "✗ Not Checked"}</span>
  );
}

function FilterBar({ children }) {
  return (
    <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 16 }}>
      {children}
    </div>
  );
}

function Select({ value, onChange, children, style }) {
  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      style={{
        background: C.navy3, color: C.text1, border: `1px solid ${C.border2}`,
        borderRadius: 6, padding: "6px 10px", fontSize: 12, cursor: "pointer",
        outline: "none", ...style,
      }}
    >{children}</select>
  );
}

function SearchInput({ value, onChange, placeholder }) {
  return (
    <input
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder || "Search…"}
      style={{
        background: C.navy3, color: C.text1, border: `1px solid ${C.border2}`,
        borderRadius: 6, padding: "6px 12px", fontSize: 12, outline: "none",
        minWidth: 200,
      }}
    />
  );
}

function TableWrap({ children }) {
  return (
    <div style={{ overflowX: "auto", borderRadius: 8, border: `1px solid ${C.border}` }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        {children}
      </table>
    </div>
  );
}

function TH({ children, onClick, sortKey, currentSort, style }) {
  const active = currentSort?.key === sortKey;
  return (
    <th
      onClick={onClick}
      style={{
        padding: "10px 14px", textAlign: "left", fontSize: 11, fontWeight: 600,
        color: active ? C.teal : C.text2, textTransform: "uppercase", letterSpacing: "0.06em",
        background: C.navy3, borderBottom: `1px solid ${C.border}`,
        cursor: onClick ? "pointer" : "default",
        userSelect: "none", whiteSpace: "nowrap", ...style,
      }}
    >
      {children}
      {onClick && <span style={{ marginLeft: 4, opacity: 0.5 }}>{active ? (currentSort.dir === "asc" ? "↑" : "↓") : "⇅"}</span>}
    </th>
  );
}

function TD({ children, style, onClick }) {
  return (
    <td style={{
      padding: "10px 14px", borderBottom: `1px solid ${C.border}`,
      color: C.text1, verticalAlign: "middle", ...style,
    }} onClick={onClick}>
      {children}
    </td>
  );
}

function TR({ children, onClick, hoverable = true }) {
  const [hov, setHov] = useState(false);
  return (
    <tr
      style={{ background: hov && hoverable ? C.navy3 : "transparent", cursor: onClick ? "pointer" : "default", transition: "background 100ms" }}
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
    >{children}</tr>
  );
}

function EmptyRow({ cols, message = "No records found" }) {
  return (
    <tr>
      <td colSpan={cols} style={{ padding: "40px 14px", textAlign: "center", color: C.text3, fontSize: 13 }}>
        {message}
      </td>
    </tr>
  );
}

// ─── DENY MODAL ───────────────────────────────────────────────────────────────
const DENY_REASONS = [
  "Controlled substance — requires in-person evaluation",
  "Too early to refill (pain management protocol)",
  "PDMP review required",
  "Dosage change needed — schedule appointment",
  "Prescription expired",
  "Medication discontinued",
  "Duplicate medication",
  "Other",
];

function DenyModal({ request, onDeny, onClose }) {
  const [reason, setReason] = useState("");
  return createPortal(
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", display: "flex",
      alignItems: "center", justifyContent: "center", zIndex: 1100,
    }} onClick={onClose}>
      <div style={{
        background: C.navy2, border: `1px solid ${C.border2}`, borderRadius: 12,
        padding: 24, width: 420, maxWidth: "94vw",
      }} onClick={e => e.stopPropagation()}>
        <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 4 }}>Deny Refill Request</div>
        <div style={{ color: C.text2, fontSize: 12, marginBottom: 16 }}>
          {request.patient_name} · {request.drug_name}
        </div>
        <label style={{ fontSize: 11, color: C.text2, fontWeight: 600 }}>REASON *</label>
        <Select value={reason} onChange={setReason} style={{ width: "100%", marginTop: 6, marginBottom: 20 }}>
          <option value="">Select a reason…</option>
          {DENY_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
        </Select>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ padding: "8px 18px", borderRadius: 6, border: `1px solid ${C.border2}`, background: "transparent", color: C.text2, cursor: "pointer", fontSize: 13 }}>Cancel</button>
          <button
            onClick={() => reason && onDeny(request.id, reason)}
            style={{ padding: "8px 18px", borderRadius: 6, border: "none", background: !reason ? C.navy4 : C.red, color: !reason ? C.text3 : "#fff", cursor: reason ? "pointer" : "not-allowed", fontSize: 13, fontWeight: 600 }}
          >Deny Refill</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── TAB 1: ACTIVE MEDICATIONS ────────────────────────────────────────────────
function ActiveMedicationsTab({ patients, prescriptions, onPatientSelect }) {
  const [search, setSearch]   = useState("");
  const [filterCtrl, setFilterCtrl] = useState("all"); // all | controlled | non-controlled
  const [filterStatus, setFilterStatus] = useState("active");
  const [filterProvider, setFilterProvider] = useState("all");
  const [sort, setSort] = useState({ key: "prescribed_date", dir: "desc" });

  const patientMap = useMemo(() => Object.fromEntries(patients.map(p => [p.id, p])), [patients]);

  const providers = useMemo(() => {
    const set = new Set(prescriptions.map(r => r.provider).filter(Boolean));
    return ["all", ...Array.from(set)];
  }, [prescriptions]);

  const rows = useMemo(() => {
    let list = prescriptions.filter(r => {
      if (filterStatus !== "all" && r.status !== filterStatus) return false;
      if (filterCtrl === "controlled" && !r.is_controlled) return false;
      if (filterCtrl === "non-controlled" && r.is_controlled) return false;
      if (filterProvider !== "all" && r.provider !== filterProvider) return false;
      if (search) {
        const pt = patientMap[r.patient_id];
        const name = pt ? `${pt.first} ${pt.last}`.toLowerCase() : "";
        const q = search.toLowerCase();
        if (!name.includes(q) && !r.drug_name.toLowerCase().includes(q)) return false;
      }
      return true;
    });
    list.sort((a, b) => {
      let av = a[sort.key] ?? "", bv = b[sort.key] ?? "";
      if (sort.key === "refills_remaining") { av = Number(av); bv = Number(bv); }
      if (av < bv) return sort.dir === "asc" ? -1 : 1;
      if (av > bv) return sort.dir === "asc" ? 1 : -1;
      return 0;
    });
    return list;
  }, [prescriptions, filterStatus, filterCtrl, filterProvider, search, sort, patientMap]);

  const toggleSort = (key) => setSort(s => s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "desc" });

  const handleRowClick = (rx) => {
    const pt = patientMap[rx.patient_id];
    if (pt) onPatientSelect(pt, "medications");
  };

  return (
    <div>
      <FilterBar>
        <SearchInput value={search} onChange={setSearch} placeholder="Search patient or drug…" />
        <Select value={filterStatus} onChange={setFilterStatus}>
          <option value="active">Active only</option>
          <option value="all">All statuses</option>
          <option value="completed">Completed</option>
          <option value="discontinued">Discontinued</option>
        </Select>
        <Select value={filterCtrl} onChange={setFilterCtrl}>
          <option value="all">All medications</option>
          <option value="controlled">Controlled only</option>
          <option value="non-controlled">Non-controlled only</option>
        </Select>
        <Select value={filterProvider} onChange={setFilterProvider}>
          {providers.map(p => <option key={p} value={p}>{p === "all" ? "All providers" : p}</option>)}
        </Select>
        <div style={{ marginLeft: "auto", color: C.text2, fontSize: 12 }}>{rows.length} record{rows.length !== 1 ? "s" : ""}</div>
      </FilterBar>

      <TableWrap>
        <thead>
          <tr>
            <TH onClick={() => toggleSort("patient_id")} sortKey="patient_id" currentSort={sort}>Patient</TH>
            <TH onClick={() => toggleSort("drug_name")} sortKey="drug_name" currentSort={sort}>Drug</TH>
            <TH>Strength</TH>
            <TH>Sig</TH>
            <TH onClick={() => toggleSort("quantity")} sortKey="quantity" currentSort={sort} style={{ textAlign: "right" }}>Qty</TH>
            <TH onClick={() => toggleSort("refills_remaining")} sortKey="refills_remaining" currentSort={sort} style={{ textAlign: "right" }}>Refills Left</TH>
            <TH>Controlled</TH>
            <TH onClick={() => toggleSort("provider")} sortKey="provider" currentSort={sort}>Prescriber</TH>
            <TH onClick={() => toggleSort("prescribed_date")} sortKey="prescribed_date" currentSort={sort}>Date Written</TH>
            <TH>Status</TH>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? <EmptyRow cols={10} /> : rows.map(rx => {
            const pt = patientMap[rx.patient_id];
            const ptName = pt ? `${pt.first} ${pt.last}` : rx.patient_id;
            return (
              <TR key={rx.id} onClick={() => handleRowClick(rx)}>
                <TD>
                  <div style={{ fontWeight: 500, color: C.teal }}>{ptName}</div>
                  {pt && <div style={{ fontSize: 11, color: C.text3 }}>{pt.mrn}</div>}
                </TD>
                <TD>
                  <div style={{ fontWeight: 500 }}>{rx.drug_name}</div>
                  <div style={{ fontSize: 11, color: C.text3 }}>{rx.dosage_form}</div>
                </TD>
                <TD style={{ color: C.text2 }}>{rx.strength}</TD>
                <TD style={{ color: C.text2, maxWidth: 220, whiteSpace: "normal", lineHeight: 1.4 }}>{rx.sig}</TD>
                <TD style={{ textAlign: "right", color: C.text2 }}>{rx.quantity}</TD>
                <TD style={{ textAlign: "right" }}>
                  <span style={{
                    fontWeight: 600,
                    color: rx.refills_remaining === 0 ? C.red : rx.refills_remaining <= 1 ? C.amber : C.green,
                  }}>{rx.refills_remaining}</span>
                  <span style={{ color: C.text3, fontSize: 11 }}> / {rx.refills}</span>
                </TD>
                <TD>
                  {rx.is_controlled
                    ? <ScheduleBadge schedule={rx.schedule} />
                    : <span style={{ fontSize: 11, color: C.text3 }}>—</span>}
                </TD>
                <TD style={{ color: C.text2 }}>{rx.provider}</TD>
                <TD style={{ color: C.text2, whiteSpace: "nowrap" }}>{fmtDate(rx.prescribed_date)}</TD>
                <TD><StatusBadge status={rx.status} /></TD>
              </TR>
            );
          })}
        </tbody>
      </TableWrap>
    </div>
  );
}

// ─── TAB 2: REFILL REQUESTS ───────────────────────────────────────────────────
function RefillRequestsTab({ patients, onPatientSelect }) {
  const [requests, setRequests] = useState(
    [...MOCK_REFILL_REQUESTS].sort((a, b) => daysSince(b.requested_date) - daysSince(a.requested_date))
  );
  const [denying, setDenying] = useState(null);

  const patientMap = useMemo(() => Object.fromEntries(patients.map(p => [p.id, p])), [patients]);

  const pending   = requests.filter(r => !r._status);
  const processed = requests.filter(r =>  r._status);

  const approve = (id) => {
    setRequests(rs => rs.map(r => r.id === id ? { ...r, _status: "approved" } : r));
  };
  const deny = (id, reason) => {
    setRequests(rs => rs.map(r => r.id === id ? { ...r, _status: "denied", _denyReason: reason } : r));
    setDenying(null);
  };

  const handleNameClick = (req, e) => {
    e.stopPropagation();
    const pt = patientMap[req.patient_id];
    if (pt) onPatientSelect(pt, "medications");
  };

  const urgencyColor = (days) => days >= 3 ? C.red : days >= 1 ? C.amber : C.text2;

  return (
    <div>
      {denying && <DenyModal request={denying} onDeny={deny} onClose={() => setDenying(null)} />}

      {pending.length === 0 && processed.length === 0 && (
        <div style={{ textAlign: "center", padding: "60px 20px", color: C.text3 }}>No refill requests</div>
      )}

      {pending.length > 0 && (
        <>
          <div style={{ fontSize: 11, fontWeight: 600, color: C.text2, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>
            Pending ({pending.length})
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 24 }}>
            {pending.map(req => {
              const days = daysSince(req.requested_date);
              return (
                <div key={req.id} style={{
                  background: C.navy2, border: `1px solid ${C.border}`,
                  borderRadius: 10, padding: "14px 18px",
                  display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap",
                }}>
                  {/* Drug + Patient */}
                  <div style={{ flex: "1 1 240px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                      <span style={{ fontWeight: 600, fontSize: 14 }}>{req.drug_name}</span>
                      {req.is_controlled && <ScheduleBadge schedule={req.schedule} />}
                    </div>
                    <div style={{ fontSize: 12, color: C.text2 }}>
                      <button
                        onClick={(e) => handleNameClick(req, e)}
                        style={{ background: "none", border: "none", padding: 0, color: C.teal, cursor: "pointer", fontSize: 12, fontWeight: 500, marginRight: 6 }}
                      >{req.patient_name}</button>
                      · {req.pharmacy}
                    </div>
                  </div>

                  {/* Days waiting */}
                  <div style={{ textAlign: "center", minWidth: 80 }}>
                    <div style={{ fontSize: 18, fontWeight: 700, color: urgencyColor(days) }}>{days}</div>
                    <div style={{ fontSize: 10, color: C.text3 }}>day{days !== 1 ? "s" : ""} waiting</div>
                  </div>

                  {/* Last fill */}
                  <div style={{ textAlign: "center", minWidth: 100 }}>
                    <div style={{ fontSize: 12, color: C.text2 }}>{fmtDate(req.requested_date)}</div>
                    <div style={{ fontSize: 10, color: C.text3 }}>requested</div>
                  </div>

                  {/* Days since last fill */}
                  <div style={{ textAlign: "center", minWidth: 90 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: req.days_since_last_fill >= 28 ? C.green : C.amber }}>
                      {req.days_since_last_fill}d
                    </div>
                    <div style={{ fontSize: 10, color: C.text3 }}>since last fill</div>
                  </div>

                  {/* Action buttons */}
                  <div style={{ display: "flex", gap: 8 }}>
                    {req.is_controlled && req.schedule === "C-II" ? (
                      <span style={{ fontSize: 11, color: C.text3, fontStyle: "italic" }}>
                        C-II: requires in-person
                      </span>
                    ) : (
                      <button
                        onClick={() => approve(req.id)}
                        style={{ padding: "7px 16px", borderRadius: 6, border: "none", background: C.teal, color: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 600 }}
                      >Approve</button>
                    )}
                    <button
                      onClick={() => setDenying(req)}
                      style={{ padding: "7px 16px", borderRadius: 6, border: `1px solid ${C.border2}`, background: "transparent", color: C.text2, cursor: "pointer", fontSize: 12 }}
                    >Deny</button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {processed.length > 0 && (
        <>
          <div style={{ fontSize: 11, fontWeight: 600, color: C.text2, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>
            Processed ({processed.length})
          </div>
          <TableWrap>
            <thead>
              <tr>
                <TH>Patient</TH>
                <TH>Drug</TH>
                <TH>Pharmacy</TH>
                <TH>Requested</TH>
                <TH>Outcome</TH>
                <TH>Reason</TH>
              </tr>
            </thead>
            <tbody>
              {processed.map(req => (
                <TR key={req.id} onClick={() => handleNameClick(req, { stopPropagation: () => {} })}>
                  <TD>
                    <span style={{ color: C.teal, fontWeight: 500, cursor: "pointer" }} onClick={(e) => handleNameClick(req, e)}>
                      {req.patient_name}
                    </span>
                  </TD>
                  <TD>{req.drug_name} {req.is_controlled && <ScheduleBadge schedule={req.schedule} />}</TD>
                  <TD style={{ color: C.text2 }}>{req.pharmacy}</TD>
                  <TD style={{ color: C.text2 }}>{fmtDate(req.requested_date)}</TD>
                  <TD><StatusBadge status={req._status} /></TD>
                  <TD style={{ color: C.text2, fontSize: 12 }}>{req._denyReason || "—"}</TD>
                </TR>
              ))}
            </tbody>
          </TableWrap>
        </>
      )}
    </div>
  );
}

// ─── TAB 3: CONTROLLED SUBSTANCES LOG ─────────────────────────────────────────
function ControlledSubstancesTab({ patients, prescriptions, onPatientSelect }) {
  const [filterSchedule, setFilterSchedule] = useState("all");
  const [filterPdmp, setFilterPdmp]         = useState("all");
  const [filterStatus, setFilterStatus]     = useState("all");
  const [search, setSearch]                 = useState("");
  const [sort, setSort] = useState({ key: "prescribed_date", dir: "desc" });

  const patientMap = useMemo(() => Object.fromEntries(patients.map(p => [p.id, p])), [patients]);

  const controlled = useMemo(() => prescriptions.filter(r => r.is_controlled), [prescriptions]);

  const rows = useMemo(() => {
    let list = controlled.filter(r => {
      if (filterSchedule !== "all" && r.schedule !== filterSchedule) return false;
      if (filterPdmp === "checked"     && !r.pdmp_checked) return false;
      if (filterPdmp === "not-checked" &&  r.pdmp_checked) return false;
      if (filterStatus !== "all" && r.status !== filterStatus) return false;
      if (search) {
        const pt = patientMap[r.patient_id];
        const name = pt ? `${pt.first} ${pt.last}`.toLowerCase() : "";
        const q = search.toLowerCase();
        if (!name.includes(q) && !r.drug_name.toLowerCase().includes(q)) return false;
      }
      return true;
    });
    list.sort((a, b) => {
      let av = a[sort.key] ?? "", bv = b[sort.key] ?? "";
      if (av < bv) return sort.dir === "asc" ? -1 : 1;
      if (av > bv) return sort.dir === "asc" ? 1 : -1;
      return 0;
    });
    return list;
  }, [controlled, filterSchedule, filterPdmp, filterStatus, search, sort, patientMap]);

  const toggleSort = (key) => setSort(s => s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "desc" });

  // Summary stats
  const totalC2      = controlled.filter(r => r.schedule === "C-II" && r.status === "active").length;
  const pdmpMissing  = controlled.filter(r => r.is_controlled && !r.pdmp_checked && r.status === "active").length;
  const activeCount  = controlled.filter(r => r.status === "active").length;

  return (
    <div>
      {/* Summary cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 12, marginBottom: 20 }}>
        {[
          { label: "Active Controlled Rx", value: activeCount, color: C.teal },
          { label: "Active C-II Scripts",  value: totalC2,     color: "#ff4444" },
          { label: "PDMP Not Checked",      value: pdmpMissing, color: pdmpMissing > 0 ? C.amber : C.green },
          { label: "Total Records",         value: controlled.length, color: C.text2 },
        ].map(s => (
          <div key={s.label} style={{ background: C.navy2, border: `1px solid ${C.border}`, borderRadius: 8, padding: "12px 16px" }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 11, color: C.text3, marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>

      <FilterBar>
        <SearchInput value={search} onChange={setSearch} placeholder="Search patient or drug…" />
        <Select value={filterSchedule} onChange={setFilterSchedule}>
          <option value="all">All schedules</option>
          <option value="C-II">C-II only</option>
          <option value="C-III">C-III only</option>
          <option value="C-IV">C-IV only</option>
          <option value="C-V">C-V only</option>
        </Select>
        <Select value={filterPdmp} onChange={setFilterPdmp}>
          <option value="all">All PDMP status</option>
          <option value="checked">PDMP checked</option>
          <option value="not-checked">PDMP not checked</option>
        </Select>
        <Select value={filterStatus} onChange={setFilterStatus}>
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="completed">Completed</option>
          <option value="discontinued">Discontinued</option>
        </Select>
        <div style={{ marginLeft: "auto", color: C.text2, fontSize: 12 }}>{rows.length} record{rows.length !== 1 ? "s" : ""}</div>
      </FilterBar>

      <TableWrap>
        <thead>
          <tr>
            <TH onClick={() => toggleSort("patient_id")} sortKey="patient_id" currentSort={sort}>Patient</TH>
            <TH onClick={() => toggleSort("drug_name")} sortKey="drug_name" currentSort={sort}>Drug</TH>
            <TH onClick={() => toggleSort("schedule")} sortKey="schedule" currentSort={sort}>Schedule</TH>
            <TH>DEA #</TH>
            <TH onClick={() => toggleSort("quantity")} sortKey="quantity" currentSort={sort} style={{ textAlign: "right" }}>Qty</TH>
            <TH onClick={() => toggleSort("prescribed_date")} sortKey="prescribed_date" currentSort={sort}>Date Written</TH>
            <TH onClick={() => toggleSort("last_filled")} sortKey="last_filled" currentSort={sort}>Last Filled</TH>
            <TH>PDMP</TH>
            <TH onClick={() => toggleSort("provider")} sortKey="provider" currentSort={sort}>Prescriber</TH>
            <TH>Status</TH>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? <EmptyRow cols={10} /> : rows.map(rx => {
            const pt = patientMap[rx.patient_id];
            const ptName = pt ? `${pt.first} ${pt.last}` : rx.patient_id;
            return (
              <TR key={rx.id} onClick={() => pt && onPatientSelect(pt, "medications")}>
                <TD>
                  <div style={{ fontWeight: 500, color: C.teal }}>{ptName}</div>
                  {pt && <div style={{ fontSize: 11, color: C.text3 }}>{pt.mrn}</div>}
                </TD>
                <TD>
                  <div style={{ fontWeight: 500 }}>{rx.drug_name}</div>
                  <div style={{ fontSize: 11, color: C.text3 }}>{rx.strength} {rx.dosage_form}</div>
                </TD>
                <TD><ScheduleBadge schedule={rx.schedule} /></TD>
                <TD style={{ fontFamily: "monospace", fontSize: 12, color: C.text2 }}>{rx.dea_number || "—"}</TD>
                <TD style={{ textAlign: "right", color: C.text2 }}>{rx.quantity}</TD>
                <TD style={{ color: C.text2, whiteSpace: "nowrap" }}>{fmtDate(rx.prescribed_date)}</TD>
                <TD style={{ color: C.text2, whiteSpace: "nowrap" }}>{fmtDate(rx.last_filled)}</TD>
                <TD><PdmpBadge checked={rx.pdmp_checked} /></TD>
                <TD style={{ color: C.text2 }}>{rx.provider}</TD>
                <TD><StatusBadge status={rx.status} /></TD>
              </TR>
            );
          })}
        </tbody>
      </TableWrap>
    </div>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
const PAGE_TABS = [
  { id: "active",      label: "Active Medications" },
  { id: "refills",     label: "Refill Requests" },
  { id: "controlled",  label: "Controlled Substances Log" },
];

export default function MedicationsPage({ patients = [], onPatientSelect }) {
  const appCtx = useApp();
  const practiceId = appCtx?.practiceId;
  const [tab, setTab] = useState("active");
  const [prescriptions, setPrescriptions] = useState([]);

  useEffect(() => {
    if (!practiceId) { setPrescriptions(isDemoMode ? MOCK_PRESCRIPTIONS : []); return; }
    db.from("prescriptions")
      .select("id, patient_id, drug_name, strength, dosage_form, sig, quantity, refills, prescribed_date, provider, status, notes, practice_id, schedule")
      .eq("practice_id", practiceId)
      .order("prescribed_date", { ascending: false })
      .limit(500)
      .then(({ data }) => setPrescriptions(data?.length ? data : (isDemoMode ? MOCK_PRESCRIPTIONS : [])));
  }, [practiceId]);

  // Pending refill count for badge
  const pendingRefills = isDemoMode ? MOCK_REFILL_REQUESTS.length : 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", gap: 0 }}>
      {/* Tab bar */}
      <div style={{
        display: "flex", gap: 4, borderBottom: `1px solid rgba(255,255,255,0.07)`,
        padding: "0 0 0 0", marginBottom: 20,
      }}>
        {PAGE_TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              padding: "10px 18px",
              border: "none",
              borderBottom: `2px solid ${tab === t.id ? C.teal : "transparent"}`,
              background: "transparent",
              color: tab === t.id ? C.teal : C.text2,
              fontWeight: tab === t.id ? 600 : 400,
              fontSize: 13, cursor: "pointer",
              display: "flex", alignItems: "center", gap: 7,
              transition: "color 150ms",
            }}
          >
            {t.label}
            {t.id === "refills" && pendingRefills > 0 && (
              <span style={{
                background: C.amber, color: C.navy, borderRadius: "50%",
                fontSize: 10, fontWeight: 700, minWidth: 18, height: 18,
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                padding: "0 5px",
              }}>{pendingRefills}</span>
            )}
            {t.id === "controlled" && (
              <span style={{
                background: C.redLt, color: C.red, borderRadius: 4,
                fontSize: 10, fontWeight: 600, padding: "1px 6px",
              }}>Audit</span>
            )}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div style={{ flex: 1, overflow: "auto" }}>
        {tab === "active" && (
          <ActiveMedicationsTab
            patients={patients}
            prescriptions={prescriptions}
            onPatientSelect={onPatientSelect}
          />
        )}
        {tab === "refills" && (
          <RefillRequestsTab
            patients={patients}
            onPatientSelect={onPatientSelect}
          />
        )}
        {tab === "controlled" && (
          <ControlledSubstancesTab
            patients={patients}
            prescriptions={prescriptions}
            onPatientSelect={onPatientSelect}
          />
        )}
      </div>
    </div>
  );
}
